import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ProjectLaborCost, ProjectLaborCostCalculator } from '../objects/project-labor-cost';
import { environment } from '../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ProjectLaborService {
  private apiUrl = environment.apiUrl +'ProjectLaborCosts';// Replace with your API endpoint
  
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  // In-memory cache for offline/demo mode
  private laborEntriesSubject = new BehaviorSubject<ProjectLaborCost[]>([]);
  public laborEntries$ = this.laborEntriesSubject.asObservable();

  get currentEntries(): ProjectLaborCost[] {
    return this.laborEntriesSubject.value;
  }

  constructor(private http: HttpClient) {}

  /**
   * GET: ProjectLaborCosts - Get all labor cost entries (matching Index action)
   */
  getAll(): Observable<ProjectLaborCost[]> {
    return this.http.get<ProjectLaborCost[]>(this.apiUrl)
      .pipe(
        map(entries => entries.map(entry => this.processLaborEntry(entry))),
        tap(entries => this.laborEntriesSubject.next(entries)),
        catchError(this.handleError<ProjectLaborCost[]>('getAll', []))
      );
  }

  /**
   * POST: ProjectLaborCosts - Create new labor entry (REST API pattern)
   */
  create(laborEntry: Partial<ProjectLaborCost>): Observable<ProjectLaborCost> {
    // Calculate computed fields before sending
    const processedEntry = ProjectLaborCostCalculator.updateCalculatedFields(laborEntry);
    
    return this.http.post<ProjectLaborCost>(this.apiUrl, processedEntry, this.httpOptions)
      .pipe(
        map(entry => this.processLaborEntry(entry)),
        tap(entry => this.addToCache(entry)),
        catchError(this.handleError<ProjectLaborCost>('create'))
      );
  }

  /**
   * GET: ProjectLaborCosts/{id} - Get labor entry by ID
   */
  getById(id: string): Observable<ProjectLaborCost | null> {
    const url = `${this.apiUrl}/${id}`;
    
    return this.http.get<ProjectLaborCost>(url)
      .pipe(
        map(entry => this.processLaborEntry(entry)),
        catchError(this.handleError<ProjectLaborCost | null>(`getById id=${id}`, null))
      );
  }

  /**
   * PUT: ProjectLaborCosts/{id} - Update existing labor entry (REST API pattern)
   */
  update(id: string, laborEntry: ProjectLaborCost): Observable<ProjectLaborCost> {
    const url = `${this.apiUrl}/${id}`;
    
    // Recalculate computed fields
    const processedEntry = ProjectLaborCostCalculator.updateCalculatedFields(laborEntry);
    processedEntry.updatedAt = new Date();
    
    return this.http.put<ProjectLaborCost>(url, processedEntry, this.httpOptions)
      .pipe(
        map(entry => this.processLaborEntry(entry)),
        tap(entry => this.updateCache(entry)),
        catchError(this.handleError<ProjectLaborCost>('update'))
      );
  }

  /**
   * DELETE: ProjectLaborCosts/{id} - Delete labor entry (REST API pattern)
   */
  deleteById(id: string): Observable<any> {
    const url = `${this.apiUrl}/${id}`;
    
    return this.http.delete(url, this.httpOptions)
      .pipe(
        tap(() => this.removeFromCache(id)),
        catchError(this.handleError<any>('deleteById'))
      );
  }

  // MVC Controller Pattern Methods (if your backend uses MVC routing)
  /**
   * GET: ProjectLaborCosts/Details/{id} - Get specific labor entry details
   */
  getDetails(id: string): Observable<ProjectLaborCost | null> {
    const url = `${this.apiUrl}/Details/${id}`;
    
    return this.http.get<ProjectLaborCost>(url)
      .pipe(
        map(entry => this.processLaborEntry(entry)),
        catchError(this.handleError<ProjectLaborCost | null>(`getDetails id=${id}`, null))
      );
  }

  /**
   * POST: ProjectLaborCosts/Create - Create new labor entry (MVC pattern)
   */
  createMvc(laborEntry: Partial<ProjectLaborCost>): Observable<ProjectLaborCost> {
    // Calculate computed fields before sending
    const processedEntry = ProjectLaborCostCalculator.updateCalculatedFields(laborEntry);
    
    return this.http.post<ProjectLaborCost>(`${this.apiUrl}/Create`, processedEntry, this.httpOptions)
      .pipe(
        map(entry => this.processLaborEntry(entry)),
        tap(entry => this.addToCache(entry)),
        catchError(this.handleError<ProjectLaborCost>('createMvc'))
      );
  }

  /**
   * GET: ProjectLaborCosts/Edit/{id} - Get labor entry for editing
   */
  getForEdit(id: string): Observable<ProjectLaborCost | null> {
    const url = `${this.apiUrl}/Edit/${id}`;
    
    return this.http.get<ProjectLaborCost>(url)
      .pipe(
        map(entry => this.processLaborEntry(entry)),
        catchError(this.handleError<ProjectLaborCost | null>(`getForEdit id=${id}`, null))
      );
  }

  /**
   * POST: ProjectLaborCosts/Edit/{id} - Update existing labor entry (MVC pattern)
   */
  editMvc(id: string, laborEntry: ProjectLaborCost): Observable<ProjectLaborCost> {
    const url = `${this.apiUrl}/Edit/${id}`;
    
    // Recalculate computed fields
    const processedEntry = ProjectLaborCostCalculator.updateCalculatedFields(laborEntry);
    processedEntry.updatedAt = new Date();
    
    return this.http.post<ProjectLaborCost>(url, processedEntry, this.httpOptions)
      .pipe(
        map(entry => this.processLaborEntry(entry)),
        tap(entry => this.updateCache(entry)),
        catchError(this.handleError<ProjectLaborCost>('editMvc'))
      );
  }

  /**
   * GET: ProjectLaborCosts/Delete/{id} - Get labor entry for deletion confirmation
   */
  getForDelete(id: string): Observable<ProjectLaborCost | null> {
    const url = `${this.apiUrl}/Delete/${id}`;
    
    return this.http.get<ProjectLaborCost>(url)
      .pipe(
        map(entry => this.processLaborEntry(entry)),
        catchError(this.handleError<ProjectLaborCost | null>(`getForDelete id=${id}`, null))
      );
  }

  /**
   * POST: ProjectLaborCosts/Delete/{id} - Confirm deletion of labor entry (MVC pattern)
   */
  deleteMvc(id: string): Observable<any> {
    const url = `${this.apiUrl}/Delete/${id}`;
    
    return this.http.post(url, {}, this.httpOptions)
      .pipe(
        tap(() => this.removeFromCache(id)),
        catchError(this.handleError<any>('deleteMvc'))
      );
  }

  getLaborEntriesByProject(projectId: number): Observable<ProjectLaborCost[]> {
    return this.http.get<ProjectLaborCost[]>(`${this.apiUrl}/ByProject/${projectId}`)
      .pipe(
        map(entries => entries.map(entry => this.processLaborEntry(entry))),
        tap(entries => this.laborEntriesSubject.next(entries)),
        catchError(this.handleError<ProjectLaborCost[]>('getLaborEntriesByProject', []))
      );
  }

  /**
   * @deprecated Use getDetails() instead
   */
  getLaborEntry(id: string): Observable<ProjectLaborCost> {
    return this.getDetails(id).pipe(
      map(entry => entry || ({} as ProjectLaborCost))
    );
  }

  /**
   * @deprecated Use create() instead
   */
  createLaborEntry(laborEntry: Partial<ProjectLaborCost>): Observable<ProjectLaborCost> {
    return this.create(laborEntry);
  }

  /**
   * @deprecated Use update() for REST API or editMvc() for MVC pattern instead
   */
  updateLaborEntry(laborEntry: ProjectLaborCost): Observable<ProjectLaborCost> {
    return this.update(laborEntry.id!, laborEntry);
  }

  /**
   * @deprecated Use deleteById() for REST API or deleteMvc() for MVC pattern instead
   */
  deleteLaborEntry(id: string): Observable<any> {
    return this.deleteById(id);
  }

  /**
   * Get labor summary for a project
   */
  getLaborSummary(projectId: number): Observable<any> {
    const url = `${this.apiUrl}/project/${projectId}/summary`;
    
    return this.http.get<any>(url)
      .pipe(
        catchError(this.handleError<any>('getLaborSummary', {
          totalCost: 0,
          billedAmount: 0,
          grossProfit: 0,
          totalHours: 0,
          entriesCount: 0
        }))
      );
  }

  /**
   * Bulk import labor entries from timesheet data
   */
  importTimesheet(projectId: number, timesheetData: any[]): Observable<ProjectLaborCost[]> {
    const url = `${this.apiUrl}/project/${projectId}/import`;
    
    return this.http.post<ProjectLaborCost[]>(url, timesheetData, this.httpOptions)
      .pipe(
        map(entries => entries.map(entry => this.processLaborEntry(entry))),
        tap(entries => this.laborEntriesSubject.next(entries)),
        catchError(this.handleError<ProjectLaborCost[]>('importTimesheet', []))
      );
  }

  /**
   * Export labor entries to various formats
   */
  exportLaborData(projectId: number, format: 'csv' | 'excel' | 'pdf' = 'csv'): Observable<Blob> {
    const url = `${this.apiUrl}/project/${projectId}/export?format=${format}`;
    
    return this.http.get(url, {
      responseType: 'blob',
      headers: this.httpOptions.headers
    }).pipe(
      catchError(this.handleError<Blob>('exportLaborData'))
    );
  }

  /**
   * Get labor cost analysis/reports
   */
  getLaborAnalysis(projectId: number, startDate?: Date, endDate?: Date): Observable<any> {
    let url = `${this.apiUrl}/project/${projectId}/analysis`;
    
    const params = [];
    if (startDate) params.push(`startDate=${startDate.toISOString()}`);
    if (endDate) params.push(`endDate=${endDate.toISOString()}`);
    
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    
    return this.http.get<any>(url)
      .pipe(
        catchError(this.handleError<any>('getLaborAnalysis', {
          costByTrade: {},
          costByWorker: {},
          profitMargins: {},
          trendsOverTime: []
        }))
      );
  }

  /**
   * Process labor entry to ensure proper date conversion and calculated fields.
   * Also normalizes PascalCase API responses to camelCase so that entry.id,
   * entry.projectId, etc. are always populated regardless of server serialization policy.
   */
  private processLaborEntry(entry: any): ProjectLaborCost {
    // Normalize PascalCase keys (e.g. "Id", "ProjectId") → camelCase ("id", "projectId")
    const n: any = {};
    Object.keys(entry).forEach(k => {
      const camel = k.charAt(0).toLowerCase() + k.slice(1);
      n[camel] = entry[k];
    });

    const processedEntry = {
      ...n,
      workDate: new Date(n.workDate),
      startTime: n.startTime ? new Date(n.startTime) : undefined,
      endTime: n.endTime ? new Date(n.endTime) : undefined,
      createdAt: n.createdAt ? new Date(n.createdAt) : new Date(),
      updatedAt: n.updatedAt ? new Date(n.updatedAt) : undefined
    };

    return ProjectLaborCostCalculator.updateCalculatedFields(processedEntry);
  }

  /**
   * Cache management methods
   */
  private addToCache(entry: ProjectLaborCost): void {
    const current = this.laborEntriesSubject.value;
    this.laborEntriesSubject.next([...current, entry]);
  }

  private updateCache(updatedEntry: ProjectLaborCost): void {
    const current = this.laborEntriesSubject.value;
    const index = current.findIndex(entry => entry.id === updatedEntry.id);
    if (index !== -1) {
      const updated = [...current];
      updated[index] = updatedEntry;
      this.laborEntriesSubject.next(updated);
    }
  }

  private removeFromCache(id: string): void {
    const current = this.laborEntriesSubject.value;
    const filtered = current.filter(entry => entry.id !== id);
    this.laborEntriesSubject.next(filtered);
  }

  /**
   * Utility methods
   */
  generateLaborReport(entries: ProjectLaborCost[]): any {
    const summary = this.calculateLaborSummary(entries);
    const byTrade = this.groupByTrade(entries);
    const byWorker = this.groupByWorker(entries);
    
    return {
      summary,
      byTrade,
      byWorker,
      entries
    };
  }

  calculateLaborSummary(entries: ProjectLaborCost[]): any {
    return entries.reduce((acc, entry) => {
      acc.totalCost += entry.totalCost;
      acc.billedAmount += entry.billedAmount;
      acc.grossProfit += entry.grossProfit;
      acc.totalHours += entry.regularHours + entry.overtimeHours + entry.doubleTimeHours;
      acc.entriesCount = entries.length;
      return acc;
    }, { totalCost: 0, billedAmount: 0, grossProfit: 0, totalHours: 0, entriesCount: 0 });
  }

  private groupByTrade(entries: ProjectLaborCost[]): any {
    return entries.reduce((acc, entry) => {
      if (!acc[entry.trade]) {
        acc[entry.trade] = {
          totalCost: 0,
          billedAmount: 0,
          grossProfit: 0,
          totalHours: 0,
          entries: []
        };
      }
      
      acc[entry.trade].totalCost += entry.totalCost;
      acc[entry.trade].billedAmount += entry.billedAmount;
      acc[entry.trade].grossProfit += entry.grossProfit;
      acc[entry.trade].totalHours += entry.regularHours + entry.overtimeHours + entry.doubleTimeHours;
      acc[entry.trade].entries.push(entry);
      
      return acc;
    }, {} as any);
  }

  private groupByWorker(entries: ProjectLaborCost[]): any {
    return entries.reduce((acc, entry) => {
      if (!acc[entry.workerName]) {
        acc[entry.workerName] = {
          totalCost: 0,
          billedAmount: 0,
          grossProfit: 0,
          totalHours: 0,
          entries: []
        };
      }
      
      acc[entry.workerName].totalCost += entry.totalCost;
      acc[entry.workerName].billedAmount += entry.billedAmount;
      acc[entry.workerName].grossProfit += entry.grossProfit;
      acc[entry.workerName].totalHours += entry.regularHours + entry.overtimeHours + entry.doubleTimeHours;
      acc[entry.workerName].entries.push(entry);
      
      return acc;
    }, {} as any);
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed:`, error);
      
      // TODO: send the error to remote logging infrastructure
      console.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
}